import { useState } from "react"
import { format } from "date-fns"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modules/shared/ui/components/alert-dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { DocumentPermissionDto } from "../../dtos/DocumentPermissionDto"
import { FolderPermissionDto } from "../../dtos/FolderPermissionDto"

type PermissionRow = DocumentPermissionDto | FolderPermissionDto

interface PermissionsTableProps {
  permissions: PermissionRow[]
  isLoading: boolean
  onRevoke: (permissionId: string) => void
  isRevoking: boolean
}

export function PermissionsTable({
  permissions,
  isLoading,
  onRevoke,
  isRevoking,
}: PermissionsTableProps) {
  const [confirmId, setConfirmId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (permissions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No permissions granted yet.
      </p>
    )
  }

  return (
    <>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                User / Role
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                Permission
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                Granted
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                Granted By
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {permissions.map((p) => {
              const subject =
                "userName" in p && p.userName
                  ? p.userName
                  : "userEmail" in p && p.userEmail
                    ? p.userEmail
                    : (p.roleName ?? p.userId ?? p.roleId ?? "—")

              return (
                <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2 font-medium">{subject}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline">{p.permissionType}</Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {format(new Date(p.grantedAt), "MMM d, yyyy")}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {p.grantedBy}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmId(p.id)}
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={() => setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke permission?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The user or role will lose access
              immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isRevoking}
              onClick={() => {
                if (confirmId) {
                  onRevoke(confirmId)
                  setConfirmId(null)
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
